/**
 * The route table.
 *
 * **Path-based, not hash-based**, and the reason is not taste: Supabase returns
 * from a password-reset link with its tokens in the URL **fragment**
 * (`#access_token=…&type=recovery`). A hash router would be parsing the same
 * bytes Supabase is using as a payload, and the collision only appears on the
 * recovery flow — the one path nobody exercises until a customer needs it.
 * The reset link lands on `/auth/confirm` (`lib/authConfirm.ts`, answered in
 * `App.tsx` before this table is consulted) and moves on to `/update-password`.
 *
 * The cost is a hosting requirement, recorded for item AM: Cloudflare Pages must
 * serve `index.html` for unknown paths (`/* /index.html 200`), or a reload on
 * `/plugins` is a 404 from the CDN rather than a route.
 *
 * No router dependency. A handful of routes and a `popstate` listener do not
 * justify one, and the pure half being a plain function is what makes it
 * testable.
 */

export const ROUTES = [
  "threads",
  "chats",
  "projects",
  "skills",
  "workflows",
  "family-builder",
  "plugins",
  "dashboard",
  "settings",
  "update-password",
] as const;

export type RouteId = (typeof ROUTES)[number];

/**
 * Where the app lands with no path, and where an unknown path falls back to.
 *
 * `threads` — the chat is the product's centre of gravity, so the app opens on
 * it the way Claude opens on a new conversation. An unknown or mangled URL lands
 * here too, which is the friendliest place for one to end up.
 */
export const DEFAULT_ROUTE: RouteId = "threads";

/**
 * The primary sidebar items, in order.
 *
 * **`threads` is deliberately absent.** A conversation is always *about* a
 * project — `/ask` carries `project_id`, and the history drawer lists per
 * project — so chat is reached by opening a project, or by "New" above this
 * list. A standalone "Chat" row offered a fourth way to the same place and
 * implied chat could stand on its own, which it cannot (owner, 2026-08-23).
 * It remains a route and the default one; only the nav item is gone.
 *
 * `dashboard` and `settings` are likewise routes reachable from the account row
 * rather than the rail: both are about *you* rather than about the work, which
 * is what that row is for. `update-password` is a landing for a recovery link.
 *
 * `skills` sits between Projects and Plugins (item BD): it is about the work,
 * like Projects, and about what is installed on this machine, like Plugins.
 *
 * `chats` — every conversation of the current project, searchable — is a route
 * without a nav item for the same reason `threads` is: it is reached from the
 * sidebar's Chats section ("View all"), which lists the newest few itself.
 *
 * `workflows` follows Family Builder: domain agents, each a card that opens its own
 * view (`docs/proposals/workflows.md`). A workflow's conversation is still a
 * chat on the `threads` route; this page is only where it is entered.
 *
 * `family-builder` follows Skills: the Revit Family Builder (#255) — Revit
 * families authored with the agent, each with its own conversation, parameters
 * and versions. It is its own page rather than a workflow card: it drives one
 * Revit instance's family document, not a project conversation.
 */
export const NAV_ROUTES: readonly RouteId[] = ["projects", "skills", "family-builder", "workflows", "plugins"];

/**
 * The locale key for each route's label — **keys, not words**.
 *
 * This table held English strings and the sidebar rendered them directly, so
 * the rail read "New / Projects / Skills / Plugins" in every language while
 * everything around it translated. Route ids are not user-facing text and this
 * module has no business holding any: it maps a path to an id, and the
 * dictionary maps an id to words.
 *
 * `Record<RouteId, string>` on purpose — adding a route without a label is then
 * a compile error rather than a raw `shell.nav.whatever` on screen.
 */
export const ROUTE_LABEL_KEYS: Record<RouteId, string> = {
  threads: "shell.nav.chat",
  chats: "chats.title",
  projects: "projects.title",
  skills: "skills.title",
  workflows: "workflows.title",
  "family-builder": "familyBuilder.nav",
  plugins: "plugins.title",
  dashboard: "dashboard.title",
  settings: "settings.title",
  "update-password": "updatePassword.title",
};

/**
 * Pathname → route. Pure.
 *
 * Unknown paths fall back rather than rendering nothing: this is a hosted SPA
 * behind a catch-all rewrite, so *every* URL on the origin arrives here,
 * including the ones a link shortener or an email client mangled.
 */
export function resolveRoute(pathname: string): RouteId {
  const first = pathname.split("/").filter(Boolean)[0];
  if (!first) return DEFAULT_ROUTE;
  return (ROUTES as readonly string[]).includes(first) ? (first as RouteId) : DEFAULT_ROUTE;
}

export function routePath(route: RouteId): string {
  return `/${route}`;
}
